// src/data/aquaticSpecies.ts
// Roddo FishIQ practical aquatic species expansion.
// Keep this curated and UX-first: common targets + edible shellfish/crustaceans,
// not a giant taxonomy dump.

import type { FishIQSpeciesSeed } from '../types';

type AquaticSeed = FishIQSpeciesSeed & {
  aquaticKind?: 'finfish' | 'crustacean' | 'mollusk' | 'cephalopod' | 'echinoderm' | 'plant';
  harvestModes?: Array<'rod_and_reel' | 'trap' | 'net' | 'dig' | 'dive' | 'rake' | 'commercial_only' | 'observe_only'>;
  waterRealms?: Array<'freshwater' | 'saltwater' | 'brackish' | 'anadromous'>;
  edibleCommonly?: boolean;
  fishiqEligible?: boolean;
  caution?: string;
};

const SHELLFISH_CAUTION = 'Shellfish and crustacean reads are habitat guidance only. Check local seasons, size limits, closures, red tide/biotoxin notices, and water-quality advisories before harvesting or eating.';
const REG_RULES_CAUTION = 'Check local seasons, bag limits, size limits, license rules, and protected areas before harvesting.';

export const AQUATIC_SPECIES_SEED: AquaticSeed[] = [
  {
    id: 'crawfish',
    slug: 'crawfish',
    commonName: 'Crawfish',
    scientificName: 'Procambarus / Faxonius spp.',
    aliases: ['Crayfish', 'Crawdads', 'Mudbugs'],
    family: 'Cambaridae',
    category: 'freshwater',
    aquaticKind: 'crustacean',
    waterRealms: ['freshwater'],
    habitats: ['pond', 'creek', 'river', 'canal', 'marsh'],
    regions: ['North America', 'Freshwater wetlands', 'Slow rivers and ponds'],
    summary: 'Freshwater crustaceans that make sense around slow water, ditches, ponds, marsh edges, and rocky creek habitat.',
    keyTraits: ['Freshwater crustacean', 'Trap/net target', 'Warm-water activity'],
    behaviorTags: ['bottom-oriented', 'cover-oriented', 'nocturnal-leaning'],
    structureTags: ['mud banks', 'rocks', 'vegetation', 'ditches'],
    activeTimes: ['evening', 'night', 'warm months'],
    feedingProfile: { primaryPrey: ['detritus', 'plants', 'small aquatic animals'], prey: ['detritus', 'snails', 'plant matter'], notes: 'Crawfish are more about habitat and water temperature than lure selection.' },
    bestBaits: ['fish scraps', 'chicken necks', 'commercial crawfish bait'],
    bestTechniques: ['small trap near cover', 'dip net around rocks', 'hand line in shallow cover'],
    bestSeasons: ['spring', 'summer'],
    bestTimesOfDay: ['evening', 'night'],
    beginnerTips: ['Look for slow freshwater with mud, rock, or vegetation.', 'Check trap rules before setting gear.'],
    advancedTips: ['Move traps until you find active edges rather than soaking one dead spot all day.'],
    harvestModes: ['trap', 'net'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'blue_crab',
    slug: 'blue-crab',
    commonName: 'Blue Crab',
    scientificName: 'Callinectes sapidus',
    aliases: ['Atlantic Blue Crab', 'Chesapeake Blue Crab'],
    family: 'Portunidae',
    category: 'saltwater',
    aquaticKind: 'crustacean',
    waterRealms: ['saltwater', 'brackish'],
    habitats: ['estuary', 'bay', 'marsh', 'inshore'],
    regions: ['Atlantic Coast', 'Gulf Coast', 'Estuaries'],
    summary: 'A brackish and saltwater crab tied to bays, marshes, tidal creeks, grass edges, and estuary structure.',
    keyTraits: ['Brackish crab', 'Tide-influenced', 'Trap/net target'],
    behaviorTags: ['tide-driven', 'bottom-oriented', 'scavenger'],
    structureTags: ['marsh edge', 'grass beds', 'tidal creeks', 'dock edges'],
    activeTimes: ['moving tide', 'warm months', 'dawn', 'dusk'],
    feedingProfile: { primaryPrey: ['bivalves', 'small fish', 'carrion'], prey: ['clams', 'mussels', 'small fish'], notes: 'Blue crab reads should lean on tide, bay/estuary habitat, and local harvest rules.' },
    bestBaits: ['menhaden', 'fish frames', 'chicken necks'],
    bestTechniques: ['crab trap', 'hand line', 'dip net on moving tide'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'dusk', 'moving tide'],
    beginnerTips: ['Start around tidal creeks, marsh edges, and docks.', 'Measure every crab and check sex/season rules.'],
    advancedTips: ['Moving water usually beats slack water for active crabs.'],
    harvestModes: ['trap', 'net'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'dungeness_crab',
    slug: 'dungeness-crab',
    commonName: 'Dungeness Crab',
    scientificName: 'Metacarcinus magister',
    aliases: ['Dungie', 'Pacific Dungeness Crab'],
    family: 'Cancridae',
    category: 'saltwater',
    aquaticKind: 'crustacean',
    waterRealms: ['saltwater'],
    habitats: ['bay', 'inshore', 'ocean'],
    regions: ['Pacific Coast', 'Bays and nearshore sand flats'],
    summary: 'A Pacific crab usually associated with sandy bays, nearshore flats, channels, and ocean-adjacent habitat.',
    keyTraits: ['Pacific crab', 'Trap target', 'Sand/mud habitat'],
    behaviorTags: ['bottom-oriented', 'scent-driven', 'tide-influenced'],
    structureTags: ['sand flats', 'bay channels', 'nearshore bottom'],
    activeTimes: ['moving tide', 'cool months', 'dawn', 'dusk'],
    feedingProfile: { primaryPrey: ['clams', 'small fish', 'carrion'], prey: ['mollusks', 'worms', 'fish scraps'], notes: 'Dungeness reads should favor sandy/muddy bay and nearshore habitat.' },
    bestBaits: ['fish carcass', 'squid', 'salmon frames'],
    bestTechniques: ['crab pot', 'crab snare', 'ring net'],
    bestSeasons: ['winter', 'spring', 'fall'],
    bestTimesOfDay: ['moving tide'],
    beginnerTips: ['Focus on legal depth/area rules and sandy bay-bottom habitat.', 'Use escape rings and check soak-time rules where required.'],
    advancedTips: ['Fresh bait and clean pot placement matter more than exact pinpoints.'],
    harvestModes: ['trap', 'net'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'stone_crab',
    slug: 'stone-crab',
    commonName: 'Stone Crab',
    scientificName: 'Menippe spp.',
    aliases: ['Florida Stone Crab'],
    family: 'Menippidae',
    category: 'saltwater',
    aquaticKind: 'crustacean',
    waterRealms: ['saltwater', 'brackish'],
    habitats: ['inshore', 'bay', 'estuary'],
    regions: ['Florida', 'Gulf Coast', 'South Atlantic'],
    summary: 'A warm coastal crab tied to rocky inshore structure, bay edges, grass, and reef-like habitat.',
    keyTraits: ['Claw harvest rules', 'Warm coastal crab', 'Structure-oriented'],
    behaviorTags: ['structure-oriented', 'bottom-oriented'],
    structureTags: ['rock piles', 'grass edges', 'inshore rubble', 'jetties'],
    activeTimes: ['warm months', 'moving tide'],
    feedingProfile: { primaryPrey: ['mollusks', 'small animals'], prey: ['oysters', 'clams', 'worms'], notes: 'Stone crab rules are highly specific; FishIQ should stay conservative.' },
    bestBaits: ['fish scraps', 'mullet', 'squid'],
    bestTechniques: ['stone crab trap'],
    bestSeasons: ['fall', 'winter', 'spring'],
    bestTimesOfDay: ['moving tide'],
    harvestModes: ['trap'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'american_lobster',
    slug: 'american-lobster',
    commonName: 'American Lobster',
    scientificName: 'Homarus americanus',
    aliases: ['Maine Lobster', 'Northern Lobster'],
    family: 'Nephropidae',
    category: 'saltwater',
    aquaticKind: 'crustacean',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore', 'bay'],
    regions: ['North Atlantic', 'New England', 'Atlantic Canada'],
    summary: 'A cold-water lobster associated with rocky bottom, ledges, kelp edges, and colder North Atlantic coastal water.',
    keyTraits: ['Cold-water lobster', 'Trap/diving target', 'Rock habitat'],
    behaviorTags: ['bottom-oriented', 'shelter-oriented', 'night-active'],
    structureTags: ['rock ledges', 'boulder fields', 'kelp edges'],
    activeTimes: ['night', 'cool water'],
    feedingProfile: { primaryPrey: ['crabs', 'mussels', 'small fish'], prey: ['mussels', 'urchins', 'small animals'], notes: 'Lobster reads should be habitat-only unless local regulations/data support more.' },
    bestBaits: ['fish racks', 'herring', 'menhaden'],
    bestTechniques: ['lobster trap', 'legal dive take'],
    bestSeasons: ['summer', 'fall'],
    bestTimesOfDay: ['night'],
    harvestModes: ['trap', 'dive'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'spiny_lobster',
    slug: 'spiny-lobster',
    commonName: 'Spiny Lobster',
    scientificName: 'Panulirus spp.',
    aliases: ['Rock Lobster', 'Caribbean Spiny Lobster', 'California Spiny Lobster'],
    family: 'Palinuridae',
    category: 'saltwater',
    aquaticKind: 'crustacean',
    waterRealms: ['saltwater'],
    habitats: ['inshore', 'ocean', 'bay'],
    regions: ['Warm coastal reefs', 'Florida', 'Caribbean', 'Southern California'],
    summary: 'A warm-water lobster tied to reefs, rocks, kelp, ledges, and crevice-heavy coastal structure.',
    keyTraits: ['Warm-water lobster', 'Reef/rock habitat', 'Nocturnal-leaning'],
    behaviorTags: ['shelter-oriented', 'night-active', 'structure-oriented'],
    structureTags: ['reefs', 'rock ledges', 'kelp beds', 'crevices'],
    activeTimes: ['night', 'warm months'],
    feedingProfile: { primaryPrey: ['mollusks', 'urchins', 'small animals'], prey: ['snails', 'clams', 'urchins'], notes: 'Spiny lobster signals should favor reef/rock/kelp habitat, not open sand.' },
    bestBaits: ['scented bait where legal'],
    bestTechniques: ['legal dive take', 'hoop net', 'trap where legal'],
    bestSeasons: ['fall', 'winter', 'spring'],
    bestTimesOfDay: ['night'],
    harvestModes: ['dive', 'net', 'trap'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'shrimp',
    slug: 'shrimp',
    commonName: 'Shrimp',
    scientificName: 'Penaeidae / Pandalidae spp.',
    aliases: ['Prawns', 'Grass Shrimp', 'Bay Shrimp'],
    family: 'Penaeidae',
    category: 'saltwater',
    aquaticKind: 'crustacean',
    waterRealms: ['saltwater', 'brackish'],
    habitats: ['estuary', 'bay', 'marsh', 'inshore'],
    regions: ['Coastal estuaries', 'Bays', 'Marsh systems'],
    summary: 'A broad shellfish group usually tied to estuaries, marsh edges, bays, grass, and tide-driven coastal water.',
    keyTraits: ['Estuary shellfish', 'Net/trap target', 'Tide-driven'],
    behaviorTags: ['schooling', 'tide-driven', 'cover-oriented'],
    structureTags: ['grass edges', 'marsh drains', 'bay flats', 'tidal creeks'],
    activeTimes: ['moving tide', 'night', 'warm months'],
    feedingProfile: { primaryPrey: ['plankton', 'detritus'], prey: ['algae', 'detritus', 'micro animals'], notes: 'Shrimp reads should care about tides, grass, marsh drains, and local netting rules.' },
    bestBaits: ['baited trap where legal', 'light/scent where legal'],
    bestTechniques: ['cast net', 'dip net', 'shrimp trap'],
    bestSeasons: ['summer', 'fall'],
    bestTimesOfDay: ['night', 'moving tide'],
    harvestModes: ['net', 'trap'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'oyster',
    slug: 'oyster',
    commonName: 'Oyster',
    scientificName: 'Crassostrea / Ostrea spp.',
    aliases: ['Eastern Oyster', 'Pacific Oyster'],
    family: 'Ostreidae',
    category: 'saltwater',
    aquaticKind: 'mollusk',
    waterRealms: ['saltwater', 'brackish'],
    habitats: ['estuary', 'bay', 'inshore'],
    regions: ['Bays', 'Estuaries', 'Shellfish beds'],
    summary: 'A filter-feeding shellfish tied to legal shellfish beds, reefs, bays, and estuaries. Safety closures matter heavily.',
    keyTraits: ['Filter feeder', 'Shellfish-bed target', 'Closure-sensitive'],
    behaviorTags: ['stationary', 'filter-feeding', 'tide-influenced'],
    structureTags: ['oyster reefs', 'shell bottom', 'tidal flats'],
    activeTimes: ['low tide', 'legal open season'],
    feedingProfile: { primaryPrey: ['plankton'], prey: ['plankton'], notes: 'Oysters are about legal open beds and water quality, not bite windows.' },
    bestBaits: [],
    bestTechniques: ['legal hand harvest', 'tongs', 'rake where legal'],
    bestSeasons: ['fall', 'winter', 'spring'],
    bestTimesOfDay: ['low tide'],
    harvestModes: ['rake', 'dig'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'hard_clam',
    slug: 'hard-clam',
    commonName: 'Hard Clam',
    scientificName: 'Mercenaria mercenaria',
    aliases: ['Quahog', 'Littleneck', 'Cherrystone'],
    family: 'Veneridae',
    category: 'saltwater',
    aquaticKind: 'mollusk',
    waterRealms: ['saltwater', 'brackish'],
    habitats: ['bay', 'estuary', 'inshore'],
    regions: ['Atlantic bays', 'Estuaries', 'Tidal flats'],
    summary: 'A burrowing clam associated with legal tidal flats, sandy/muddy bay bottoms, and estuary shellfish grounds.',
    keyTraits: ['Burrowing clam', 'Low-tide target', 'Closure-sensitive'],
    behaviorTags: ['burrowing', 'filter-feeding', 'tide-linked'],
    structureTags: ['sand flats', 'mud flats', 'tidal flats'],
    activeTimes: ['low tide', 'legal open season'],
    feedingProfile: { primaryPrey: ['plankton'], prey: ['plankton'], notes: 'Clam reads are habitat/legality reads, not feeding predictions.' },
    bestBaits: [],
    bestTechniques: ['rake', 'dig', 'scratch legal flats'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['low tide'],
    harvestModes: ['dig', 'rake'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'razor_clam',
    slug: 'razor-clam',
    commonName: 'Razor Clam',
    scientificName: 'Siliqua / Ensis spp.',
    aliases: ['Pacific Razor Clam', 'Atlantic Razor Clam'],
    family: 'Pharidae',
    category: 'saltwater',
    aquaticKind: 'mollusk',
    waterRealms: ['saltwater'],
    habitats: ['inshore', 'ocean', 'bay'],
    regions: ['Surf beaches', 'Sandy tidal flats'],
    summary: 'A fast-burrowing clam associated with sandy beaches, surf zones, and legal low-tide digging windows.',
    keyTraits: ['Surf clam', 'Low-tide dig target', 'Closure-sensitive'],
    behaviorTags: ['burrowing', 'surf-zone', 'tide-linked'],
    structureTags: ['sandy beach', 'low-tide flat', 'surf edge'],
    activeTimes: ['low tide', 'legal open season'],
    feedingProfile: { primaryPrey: ['plankton'], prey: ['plankton'], notes: 'Razor clam reads should emphasize tide, beach habitat, and closures.' },
    bestBaits: [],
    bestTechniques: ['clam gun', 'clam shovel', 'dig legal shows'],
    bestSeasons: ['fall', 'winter', 'spring'],
    bestTimesOfDay: ['low tide'],
    harvestModes: ['dig'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'mussel',
    slug: 'mussel',
    commonName: 'Mussel',
    scientificName: 'Mytilus spp.',
    aliases: ['Blue Mussel', 'California Mussel'],
    family: 'Mytilidae',
    category: 'saltwater',
    aquaticKind: 'mollusk',
    waterRealms: ['saltwater'],
    habitats: ['inshore', 'bay', 'ocean'],
    regions: ['Rocky coasts', 'Bays', 'Intertidal zones'],
    summary: 'A filter-feeding shellfish attached to rocks, pilings, reefs, and intertidal edges. Closures and biotoxins are critical.',
    keyTraits: ['Attached shellfish', 'Intertidal', 'Closure-sensitive'],
    behaviorTags: ['stationary', 'filter-feeding', 'tide-linked'],
    structureTags: ['rocks', 'pilings', 'jetties', 'intertidal reefs'],
    activeTimes: ['low tide', 'legal open season'],
    feedingProfile: { primaryPrey: ['plankton'], prey: ['plankton'], notes: 'Mussel reads are location/safety/legal checks first.' },
    bestBaits: [],
    bestTechniques: ['hand harvest where legal'],
    bestSeasons: ['winter', 'spring', 'fall'],
    bestTimesOfDay: ['low tide'],
    harvestModes: ['rake', 'dig'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'bay_scallop',
    slug: 'bay-scallop',
    commonName: 'Bay Scallop',
    scientificName: 'Argopecten irradians',
    aliases: ['Scallop'],
    family: 'Pectinidae',
    category: 'saltwater',
    aquaticKind: 'mollusk',
    waterRealms: ['saltwater'],
    habitats: ['bay', 'estuary', 'inshore'],
    regions: ['Seagrass bays', 'Clear shallow coastal water'],
    summary: 'A small scallop associated with clean seagrass flats and legal shallow bay harvest areas.',
    keyTraits: ['Seagrass shellfish', 'Dive/snorkel target', 'Closure-sensitive'],
    behaviorTags: ['grass-associated', 'filter-feeding'],
    structureTags: ['seagrass flats', 'clear bay shallows'],
    activeTimes: ['day', 'legal open season'],
    feedingProfile: { primaryPrey: ['plankton'], prey: ['plankton'], notes: 'Bay scallop reads should favor clear grass flats and current legal seasons.' },
    bestBaits: [],
    bestTechniques: ['snorkel', 'hand gather where legal'],
    bestSeasons: ['summer'],
    bestTimesOfDay: ['day'],
    harvestModes: ['dive'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: SHELLFISH_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'squid',
    slug: 'squid',
    commonName: 'Squid',
    scientificName: 'Loligo / Doryteuthis spp.',
    aliases: ['Calamari Squid', 'Market Squid'],
    family: 'Loliginidae',
    category: 'saltwater',
    aquaticKind: 'cephalopod',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore', 'bay'],
    regions: ['Nearshore coasts', 'Piers', 'Harbors', 'Bays'],
    summary: 'A mobile cephalopod often associated with night lights, piers, harbors, bait schools, and nearshore coastal water.',
    keyTraits: ['Night-light target', 'Schooling', 'Jig target'],
    behaviorTags: ['schooling', 'light-attracted', 'bait-oriented'],
    structureTags: ['piers', 'harbors', 'night lights', 'bait balls'],
    activeTimes: ['night', 'dusk'],
    feedingProfile: { primaryPrey: ['small fish', 'crustaceans'], prey: ['baitfish', 'shrimp'], notes: 'Squid reads can behave more like a fishing read than a stationary shellfish read.' },
    bestBaits: ['squid jig', 'small glow jig'],
    bestTechniques: ['vertical jig under lights', 'slow lift/drop'],
    bestSeasons: ['fall', 'winter', 'spring'],
    bestTimesOfDay: ['night', 'dusk'],
    harvestModes: ['rod_and_reel', 'net'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'octopus',
    slug: 'octopus',
    commonName: 'Octopus',
    scientificName: 'Octopus spp.',
    aliases: ['Common Octopus'],
    family: 'Octopodidae',
    category: 'saltwater',
    aquaticKind: 'cephalopod',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore', 'bay'],
    regions: ['Rocky coasts', 'Reefs', 'Kelp and ledges'],
    summary: 'A smart, structure-oriented cephalopod associated with reefs, rocks, dens, kelp, and coastal ledges.',
    keyTraits: ['Reef/rock cephalopod', 'Den-oriented', 'Regulation-sensitive'],
    behaviorTags: ['shelter-oriented', 'night-active', 'structure-oriented'],
    structureTags: ['rock dens', 'reefs', 'kelp edges', 'ledges'],
    activeTimes: ['night', 'dusk'],
    feedingProfile: { primaryPrey: ['crabs', 'mollusks', 'small fish'], prey: ['crabs', 'clams', 'shrimp'], notes: 'Octopus reads are cautious habitat guidance and should respect local take/protection rules.' },
    bestBaits: ['scented jig where legal'],
    bestTechniques: ['observe/dive where legal', 'jig near rocky cover where legal'],
    bestSeasons: ['summer', 'fall'],
    bestTimesOfDay: ['night'],
    harvestModes: ['dive', 'rod_and_reel', 'observe_only'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'red_sea_urchin',
    slug: 'red-sea-urchin',
    commonName: 'Red Sea Urchin',
    scientificName: 'Mesocentrotus franciscanus',
    aliases: ['Sea Urchin', 'Uni'],
    family: 'Strongylocentrotidae',
    category: 'saltwater',
    aquaticKind: 'echinoderm',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore'],
    regions: ['Pacific rocky reefs', 'Kelp forests'],
    summary: 'An edible echinoderm associated with rocky reefs and kelp habitat. Harvest reads must stay legal/safety-first.',
    keyTraits: ['Kelp/rock habitat', 'Dive target', 'Rule-sensitive'],
    behaviorTags: ['stationary', 'kelp-associated'],
    structureTags: ['kelp forest', 'rocky reef', 'ledge habitat'],
    activeTimes: ['day', 'legal open season'],
    feedingProfile: { primaryPrey: ['kelp', 'algae'], prey: ['kelp', 'algae'], notes: 'Urchin reads are habitat guidance, not activity predictions.' },
    bestBaits: [],
    bestTechniques: ['legal dive harvest'],
    bestSeasons: ['winter', 'spring', 'summer', 'fall'],
    bestTimesOfDay: ['day'],
    harvestModes: ['dive'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'abalone',
    slug: 'abalone',
    commonName: 'Abalone',
    scientificName: 'Haliotis spp.',
    aliases: ['Red Abalone', 'Pinto Abalone'],
    family: 'Haliotidae',
    category: 'saltwater',
    aquaticKind: 'mollusk',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore'],
    regions: ['Rocky coasts', 'Kelp forests'],
    summary: 'A kelp/rock-associated marine snail. Many areas have strict closures or protections, so FishIQ treats this as cautious habitat info.',
    keyTraits: ['Kelp/rock mollusk', 'Highly regulated', 'Dive/shore target where legal'],
    behaviorTags: ['stationary', 'kelp-associated', 'structure-oriented'],
    structureTags: ['kelp forest', 'rocky reef', 'intertidal rocks'],
    activeTimes: ['low tide', 'legal open season'],
    feedingProfile: { primaryPrey: ['kelp', 'algae'], prey: ['kelp', 'algae'], notes: 'Abalone reads must be conservative because closures and protections are common.' },
    bestBaits: [],
    bestTechniques: ['observe only unless clearly legal', 'legal dive/shore take where open'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['low tide'],
    harvestModes: ['dive', 'observe_only'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: 'Many abalone fisheries are closed or heavily restricted. Verify current local rules before any take.',
    confidence: 'editorial',
  },
  {
    id: 'white_sturgeon',
    slug: 'white-sturgeon',
    commonName: 'White Sturgeon',
    scientificName: 'Acipenser transmontanus',
    aliases: ['Sturgeon'],
    family: 'Acipenseridae',
    category: 'both',
    aquaticKind: 'finfish',
    waterRealms: ['freshwater', 'brackish', 'anadromous'],
    habitats: ['river', 'estuary', 'bay'],
    regions: ['Pacific rivers', 'Estuaries', 'Large river systems'],
    summary: 'A large bottom-oriented fish associated with deep river holes, estuary channels, current seams, and strict harvest rules.',
    keyTraits: ['Large river fish', 'Bottom-oriented', 'Highly regulated'],
    behaviorTags: ['bottom-feeding', 'current-oriented', 'anadromous'],
    structureTags: ['deep holes', 'channel edges', 'current seams'],
    activeTimes: ['dawn', 'dusk', 'night'],
    feedingProfile: { primaryPrey: ['shrimp', 'clams', 'worms', 'small fish'], prey: ['ghost shrimp', 'lamprey', 'herring'], notes: 'Sturgeon reads should be cautious because regulations are strict and water systems are specific.' },
    bestBaits: ['shrimp', 'herring', 'lamprey where legal', 'worms'],
    bestTechniques: ['anchored bait on current seams', 'bottom rig'],
    bestSeasons: ['winter', 'spring', 'summer'],
    bestTimesOfDay: ['dawn', 'dusk', 'night'],
    harvestModes: ['rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'snakehead',
    slug: 'snakehead',
    commonName: 'Snakehead',
    scientificName: 'Channa / Parachanna spp.',
    aliases: ['Northern Snakehead'],
    family: 'Channidae',
    category: 'freshwater',
    aquaticKind: 'finfish',
    waterRealms: ['freshwater'],
    habitats: ['pond', 'creek', 'river', 'canal', 'marsh'],
    regions: ['Invasive ranges', 'Weedy freshwater', 'Slow warm water'],
    summary: 'A predatory freshwater fish often associated with shallow weeds, canals, marshy edges, and warm slow water where present.',
    keyTraits: ['Invasive in many areas', 'Weed-oriented predator', 'Topwater-capable'],
    behaviorTags: ['ambush predator', 'cover-oriented', 'warm-water'],
    structureTags: ['grass mats', 'canals', 'marsh edges', 'shallow cover'],
    activeTimes: ['dawn', 'dusk', 'warm months'],
    feedingProfile: { primaryPrey: ['small fish', 'frogs', 'crayfish'], prey: ['baitfish', 'frogs'], notes: 'Snakehead signals should only appear where the species is plausible/known.' },
    bestBaits: ['frog lure', 'swimbait', 'spinnerbait'],
    bestTechniques: ['topwater over weeds', 'pitch to holes in mats'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'dusk'],
    harvestModes: ['rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: 'Snakehead rules vary and may require dispatch/no transport alive. Check local invasive species laws.',
    confidence: 'editorial',
  },
  {
    id: 'yellow_perch',
    slug: 'yellow-perch',
    commonName: 'Yellow Perch',
    scientificName: 'Perca flavescens',
    aliases: ['Perch'],
    family: 'Percidae',
    category: 'freshwater',
    aquaticKind: 'finfish',
    waterRealms: ['freshwater'],
    habitats: ['lake', 'reservoir', 'river', 'pond'],
    regions: ['Northern lakes', 'Reservoirs', 'Slow rivers'],
    summary: 'A schooling freshwater panfish commonly tied to weed edges, flats, drop-offs, and baitfish in lakes and reservoirs.',
    keyTraits: ['Schooling panfish', 'Cool-water tolerant', 'Light tackle'],
    behaviorTags: ['schooling', 'bait-oriented', 'structure-edge'],
    structureTags: ['weed edge', 'flats', 'drop-offs', 'docks'],
    activeTimes: ['dawn', 'day', 'dusk'],
    feedingProfile: { primaryPrey: ['minnows', 'insects', 'small crustaceans'], prey: ['worms', 'small minnows'], notes: 'Perch reads should favor schools and edges over isolated cover.' },
    bestBaits: ['worms', 'minnows', 'small jigs'],
    bestTechniques: ['small jigging spoon', 'slip bobber', 'slow bottom hop'],
    bestSeasons: ['winter', 'spring', 'fall'],
    bestTimesOfDay: ['day', 'dawn', 'dusk'],
    harvestModes: ['rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'rainbow_smelt',
    slug: 'rainbow-smelt',
    commonName: 'Rainbow Smelt',
    scientificName: 'Osmerus mordax',
    aliases: ['Smelt'],
    family: 'Osmeridae',
    category: 'both',
    aquaticKind: 'finfish',
    waterRealms: ['freshwater', 'saltwater', 'anadromous'],
    habitats: ['lake', 'river', 'bay', 'inshore'],
    regions: ['Northern lakes', 'Coastal rivers', 'Coldwater systems'],
    summary: 'A small schooling fish found in cold lakes and coastal systems, often relevant around runs, ice seasons, or bait concentrations.',
    keyTraits: ['Small schooling fish', 'Cold-water', 'Run/season driven'],
    behaviorTags: ['schooling', 'cold-water', 'seasonal-run'],
    structureTags: ['river mouths', 'lake basins', 'shoreline lights'],
    activeTimes: ['night', 'dusk', 'seasonal runs'],
    feedingProfile: { primaryPrey: ['plankton', 'small crustaceans'], prey: ['zooplankton'], notes: 'Smelt reads are very seasonal and local.' },
    bestBaits: ['tiny jigs', 'sabiki-style rigs where legal'],
    bestTechniques: ['dip net where legal', 'small jig under lights'],
    bestSeasons: ['winter', 'spring'],
    bestTimesOfDay: ['night', 'dusk'],
    harvestModes: ['net', 'rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'black_sea_bass',
    slug: 'black-sea-bass',
    commonName: 'Black Sea Bass',
    scientificName: 'Centropristis striata',
    aliases: ['Sea Bass'],
    family: 'Serranidae',
    category: 'saltwater',
    aquaticKind: 'finfish',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore', 'bay'],
    regions: ['Atlantic Coast', 'Nearshore reefs', 'Wrecks'],
    summary: 'A structure-oriented saltwater fish found around reefs, wrecks, rocks, and hard-bottom coastal areas.',
    keyTraits: ['Reef/wreck fish', 'Bottom-oriented', 'Structure-loving'],
    behaviorTags: ['structure-oriented', 'bottom-feeding', 'schooling'],
    structureTags: ['wrecks', 'reefs', 'rock piles', 'hard bottom'],
    activeTimes: ['day', 'dawn', 'dusk'],
    feedingProfile: { primaryPrey: ['crabs', 'squid', 'small fish'], prey: ['crabs', 'shrimp', 'squid'], notes: 'Black sea bass reads should favor hard structure over open sand.' },
    bestBaits: ['squid strips', 'jigs', 'clam strips'],
    bestTechniques: ['bottom rig', 'vertical jig', 'slow pitch over structure'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day'],
    harvestModes: ['rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'sheepshead',
    slug: 'sheepshead',
    commonName: 'Sheepshead',
    scientificName: 'Archosargus probatocephalus',
    aliases: ['Convict Fish'],
    family: 'Sparidae',
    category: 'saltwater',
    aquaticKind: 'finfish',
    waterRealms: ['saltwater', 'brackish'],
    habitats: ['inshore', 'bay', 'estuary'],
    regions: ['Atlantic Coast', 'Gulf Coast', 'Inshore structure'],
    summary: 'A structure-focused inshore fish that feeds heavily around pilings, rocks, jetties, oysters, and bridge structure.',
    keyTraits: ['Structure nibbler', 'Crab/barnacle eater', 'Inshore'],
    behaviorTags: ['structure-oriented', 'bottom-feeding', 'tidal'],
    structureTags: ['dock pilings', 'bridge pilings', 'jetties', 'oyster bars'],
    activeTimes: ['moving tide', 'day'],
    feedingProfile: { primaryPrey: ['crabs', 'barnacles', 'shrimp'], prey: ['fiddler crabs', 'sand fleas', 'shrimp'], notes: 'Sheepshead reads should hug hard inshore structure.' },
    bestBaits: ['fiddler crab', 'shrimp', 'sand flea'],
    bestTechniques: ['tight-line pilings', 'small hook near structure'],
    bestSeasons: ['winter', 'spring'],
    bestTimesOfDay: ['moving tide', 'day'],
    harvestModes: ['rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
  {
    id: 'yellowtail_amberjack',
    slug: 'yellowtail-amberjack',
    commonName: 'Yellowtail Amberjack',
    scientificName: 'Seriola lalandi',
    aliases: ['Yellowtail', 'California Yellowtail'],
    family: 'Carangidae',
    category: 'saltwater',
    aquaticKind: 'finfish',
    waterRealms: ['saltwater'],
    habitats: ['ocean', 'inshore'],
    regions: ['Pacific Coast', 'Kelp beds', 'Islands and reefs'],
    summary: 'A strong coastal pelagic tied to bait, kelp lines, reefs, islands, and current edges.',
    keyTraits: ['Pelagic predator', 'Bait-oriented', 'Kelp/current edges'],
    behaviorTags: ['bait-oriented', 'current-oriented', 'schooling'],
    structureTags: ['kelp lines', 'islands', 'reef edges', 'current seams'],
    activeTimes: ['dawn', 'day', 'dusk'],
    feedingProfile: { primaryPrey: ['sardines', 'mackerel', 'squid'], prey: ['baitfish', 'squid'], notes: 'Yellowtail reads should care about current, bait, and structure edges.' },
    bestBaits: ['live bait', 'surface iron', 'yo-yo jig'],
    bestTechniques: ['slow troll live bait', 'cast surface iron', 'vertical jig current edges'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day'],
    harvestModes: ['rod_and_reel'],
    edibleCommonly: true,
    fishiqEligible: true,
    caution: REG_RULES_CAUTION,
    confidence: 'editorial',
  },
];
